/* @flow */

import L from 'leaflet';
import Globals from '../../constants/Globals';
import { isPlatformMac } from '../../utils/WidgetUtils';

L.Map.mergeOptions({
  mapGestureHandlingOptions: {
    translations: {}
  }
});

const MapGestureHandling = L.Handler.extend({

  _cnScrollWarning: 'leaflet-gesture-handling-scroll-warning',

  _cnTouchWarning: 'leaflet-gesture-handling-touch-warning',

  _attrScrollContent: 'data-gesture-handling-scroll-content',

  _attrTouchContent: 'data-gesture-handling-touch-content',

  _isScrolling: false,

  _draggingMap: false,

  addHooks: function() {
    this._disableInteractions();
    L.DomEvent.on(this._map._container, 'mousewheel', this._handleScroll, this);

    /*
     * Use native event listeners instead of L.DomEvent due to issues with Android
     * touch events turning into pointer events
     */
    this._handleTouch = this._handleTouch.bind(this);
    this._map._container.addEventListener('touchstart', this._handleTouch);
    this._map._container.addEventListener('touchmove', this._handleTouch);
    this._map._container.addEventListener('touchend', this._handleTouch);
    this._map._container.addEventListener('touchcancel', this._handleTouch);

    L.DomEvent.on(this._map, 'movestart', this._handleDragging, this);
    L.DomEvent.on(this._map, 'move', this._handleDragging, this);
    L.DomEvent.on(this._map, 'moveend', this._handleDragging, this);

    L.DomEvent.on(this._map, 'mouseover', this._handleMouseOver, this);
    L.DomEvent.on(this._map, 'mouseout', this._handleMouseOut, this);
  },

  removeHooks: function() {
    this._enableInteractions();
    L.DomEvent.off(this._map._container, 'mousewheel', this._handleScroll, this);

    this._map._container.removeEventListener('touchstart', this._handleTouch);
    this._map._container.removeEventListener('touchmove', this._handleTouch);
    this._map._container.removeEventListener('touchend', this._handleTouch);
    this._map._container.removeEventListener('touchcancel', this._handleTouch);

    L.DomEvent.off(this._map, 'movestart', this._handleDragging, this);
    L.DomEvent.off(this._map, 'move', this._handleDragging, this);
    L.DomEvent.off(this._map, 'moveend', this._handleDragging, this);

    L.DomEvent.off(this._map, 'mouseover', this._handleMouseOver, this);
    L.DomEvent.off(this._map, 'mouseout', this._handleMouseOut, this);
  },

  _disableInteractions: function() {
    this._map.dragging.disable();
    this._map.scrollWheelZoom.disable();
    if (this._map.tap) this._map.tap.disable();
  },

  _enableInteractions: function() {
    this._map.dragging.enable();
    this._map.scrollWheelZoom.enable();
    if (this._map.tap) this._map.tap.enable();
  },

  _getGestureTranslationTouch: function() {
    return this._map.options.mapGestureHandlingOptions.translations.touch ||
      Globals.MAPS_GESTURES_ZOOM_TOUCH_LABEL;
  },

  _getGestureTranslationScroll: function() {
    return this._map.options.mapGestureHandlingOptions.translations.scroll ||
      Globals.MAPS_GESTURES_ZOOM_SCROLL_LABEL;
  },

  _getGestureTranslationScrollMac: function() {
    return this._map.options.mapGestureHandlingOptions.translations.scrollMac ||
      Globals.MAPS_GESTURES_ZOOM_SCROLL_MAC_LABEL;
  },

  _handleDragging: function(event: Event) {
    if (event.type === 'movestart' || event.type === 'move') this._draggingMap = true;
    else if (event.type === 'moveend') this._draggingMap = false;
  },

  _handleMouseOver: function() {
    this._enableInteractions();
  },

  _handleMouseOut: function() {
    if (!this._draggingMap) this._disableInteractions();
  },

  _handleScroll: function(event: Event) {
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      this._map.scrollWheelZoom.enable();
    }
    else {
      this._map.scrollWheelZoom.disable();

      L.DomUtil.addClass(this._map._container, this._cnScrollWarning);

      const content = isPlatformMac(window.navigator.platform) ?
        this._getGestureTranslationScrollMac() : this._getGestureTranslationScroll();

      this._map._container.setAttribute(this._attrScrollContent, content);

      clearTimeout(this._isScrolling);
      this._isScrolling = setTimeout(() => {
        const warnings = document.getElementsByClassName(this._cnScrollWarning);
        for (let warning of warnings) {
          L.DomUtil.removeClass(warning, this._cnScrollWarning);
        }
      }, 500);
    }
  },

  _handleTouch: function(event: TouchEvent) {

    const touchesCount = event.touches.length;
    const ignoreList = [
      'leaflet-control-minimap',
      'leaflet-interactive',
      'leaflet-popup-content',
      'leaflet-popup-content-wrapper',
      'leaflet-popup-close-button',
      'leaflet-control-zoom-in',
      'leaflet-control-zoom-out'
    ];

    let ignoreElement = false;
    for (let ignoreListItem of ignoreList) {
      if (L.DomUtil.hasClass(event.target, ignoreListItem)) ignoreElement = true;
    }

    if (ignoreElement) {
      if (L.DomUtil.hasClass(event.target, 'leaflet-interactive') &&
      event.type === 'touchmove' && touchesCount === 1) {
        this._disableInteractions();
        L.DomUtil.addClass(this._map._container, this._cnTouchWarning);
      }
      else {
        L.DomUtil.removeClass(this._map._container, this._cnTouchWarning);
      }
      return;
    }

    if (event.type !== 'touchmove' && event.type !== 'touchstart') {
      L.DomUtil.removeClass(this._map._container, this._cnTouchWarning);
      return;
    }

    if (touchesCount === 1) {
      this._disableInteractions();
      L.DomUtil.addClass(this._map._container, this._cnTouchWarning);
      this._map._container.setAttribute(this._attrTouchContent, this._getGestureTranslationTouch());
    }
    else {
      this._enableInteractions();
      L.DomUtil.removeClass(this._map._container, this._cnTouchWarning);
    }
  }
});

export default MapGestureHandling;
